'use client'

import { useRef, useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'

const fullPic = '/portraits/full-pic.webp'
const fullPicDots = '/portraits/full-pic-dots.webp'

gsap.registerPlugin(ScrollTrigger)

/* ─── HUD chrome (mirrors Gallery / Portal primitives) ─── */
const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'
const LBL: CSSProperties = { fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }
const LBL2: CSSProperties = { ...LBL, color: 'var(--color-stone)' }

// Rotating targeting reticle — fills its (square) parent so it rings the subject.
function Reticle() {
  const c = 'var(--color-fire)'
  return (
    <svg aria-hidden viewBox="0 0 100 100" className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%', opacity: 0.55 }}>
      <g style={{ transformOrigin: 'center', animation: 'hud-spin 30s linear infinite' }}>
        <circle cx="50" cy="50" r="48.5" fill="none" stroke={c} strokeWidth="0.3" strokeDasharray="0.4 3.6" opacity="0.5" />
        <line x1="50" y1="0.5" x2="50" y2="6" stroke={c} strokeWidth="0.6" opacity="0.85" />
        <line x1="50" y1="94" x2="50" y2="99.5" stroke={c} strokeWidth="0.6" opacity="0.85" />
        <line x1="0.5" y1="50" x2="6" y2="50" stroke={c} strokeWidth="0.6" opacity="0.85" />
        <line x1="94" y1="50" x2="99.5" y2="50" stroke={c} strokeWidth="0.6" opacity="0.85" />
      </g>
      <g style={{ transformOrigin: 'center', animation: 'hud-spin-rev 22s linear infinite' }}>
        <circle cx="50" cy="50" r="41" fill="none" stroke={c} strokeWidth="0.45" strokeDasharray="16 84" opacity="0.6" />
        <circle cx="50" cy="50" r="41" fill="none" stroke={c} strokeWidth="0.45" strokeDasharray="16 84" strokeDashoffset="50" opacity="0.6" />
      </g>
    </svg>
  )
}

interface VoidProps {
  isReady?: boolean
}

/**
 * Void — Scene 00: Hero → "[00] IDENTITY.TERMINAL — SUBJECT ACQUIRED"
 * The machine boots, scans the void, and LOCKS onto a face — the WebGL particle
 * portrait is the system's first acquired identity, framed in a reticle crosshair.
 * Entrance gated by isReady (preloader). WebGL invariants preserved: #void (200vh +
 * pin) and #void-portrait (the particle lock box) are untouched.
 */
export default function Void({ isReady = false }: VoidProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const glowRef = useRef<HTMLDivElement>(null)
  const lineRef1 = useRef<HTMLSpanElement>(null)
  const lineRef2 = useRef<HTMLSpanElement>(null)
  const portraitRef = useRef<HTMLDivElement>(null)

  // SSR-safe: default false on the server / first client render, corrected in the effect below.
  const [isMobile, setIsMobile] = useState(false)
  const [locked, setLocked] = useState(false) // ◌ ACQUIRING → ◉ LOCK ACQUIRED on the lock beat

  /* Consistent edge margin — matches nav inline padding */
  const edge = 'clamp(1.5rem, 4vw, 3.5rem)'
  // Phone gets a big name (fills the width) like the desktop composition, so the large
  // portrait reads OVER it; desktop keeps its huge scaled name.
  // 12vw (not 13) — at 390px "ABDELRAHMAN" was bleeding ~19px off each edge
  const nameSize = isMobile ? 'clamp(2.2rem, 12vw, 5rem)' : 'clamp(2.5rem, 10vw, 20rem)'

  // ── ScrollTrigger setup — runs once, never re-created ──
  useGSAP(
    () => {
      const ctx = containerRef.current
      if (!ctx) return

      // Pin sticky viewport
      ScrollTrigger.create({
        trigger: ctx,
        start: 'top top',
        end: 'bottom bottom',
        pin: stickyRef.current,
        pinSpacing: false,
      })

      // Scroll exit — status bar, name, coords, and the lock overlay fade up
      const statusbar = ctx.querySelector<HTMLElement>('.hero-statusbar')
      const coords = ctx.querySelector<HTMLElement>('.hero-coords')
      const lock = ctx.querySelector<HTMLElement>('.hero-lock')
      const indicator = indicatorRef.current
      const portrait = portraitRef.current

      gsap.to([statusbar, contentRef.current, coords, lock], {
        scrollTrigger: {
          trigger: ctx,
          start: '30% top',
          end: '70% top',
          scrub: 1,
        },
        y: -50,
        scale: 0.94,
        opacity: 0,
        ease: 'none',
      })

      // Portrait parallax + fade on scroll
      if (portrait) {
        gsap.to(portrait, {
          scrollTrigger: {
            trigger: ctx,
            start: '20% top',
            end: '65% top',
            scrub: 1,
          },
          y: -80,
          scale: 1.05,
          opacity: 0,
          ease: 'none',
        })
      }

      gsap.to(indicator, {
        scrollTrigger: {
          trigger: ctx,
          start: '12% top',
          end: '28% top',
          scrub: 0.5,
        },
        opacity: 0,
        ease: 'none',
      })
    },
    { scope: containerRef },
  )

  // ── Entrance animations — fires once when preloader completes ──
  useEffect(() => {
    if (!isReady) return
    const ctx = containerRef.current
    if (!ctx) return

    const statusbar = ctx.querySelector<HTMLElement>('.hero-statusbar')
    const corners = ctx.querySelectorAll<HTMLElement>('.hero-corner')
    const line1 = ctx.querySelector<HTMLElement>('.hero-line-1')
    const line2 = ctx.querySelector<HTMLElement>('.hero-line-2')
    const reticle = ctx.querySelector<HTMLElement>('.hero-lock-ring')
    const tagline = ctx.querySelector<HTMLElement>('.hero-tagline')
    const taglineText = ctx.querySelector<HTMLElement>('.hero-tagline-text')
    const coords = ctx.querySelector<HTMLElement>('.hero-coords')
    const indicator = indicatorRef.current

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduce) {
      gsap.set([statusbar, corners, line1, line2, reticle, tagline, coords, indicator], { opacity: 1, x: 0, y: 0, scale: 1, autoAlpha: 1 })
      if (taglineText) gsap.set(taglineText, { width: 'auto' })
      setLocked(true)
      return
    }

    const vw = window.innerWidth
    const vh = window.innerHeight

    const intro = gsap.timeline({ delay: 0.15 })
    intro
      .fromTo(statusbar, { y: -8, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' })
      .fromTo(corners, { autoAlpha: 0, scale: 0.6 }, { autoAlpha: 0.5, scale: 1, duration: 0.5, stagger: 0.06, ease: 'power3.out' }, '-=0.2')
      // Each line FORGES up out of the void (clip-wipe) rather than a plain slide
      .fromTo(line1, { clipPath: 'inset(100% 0 0 0)', y: 28, opacity: 0 }, { clipPath: 'inset(0% 0 0 0)', y: 0, opacity: 1, duration: 1.0, ease: 'power4.out' }, '-=0.3')
      .fromTo(line2, { clipPath: 'inset(100% 0 0 0)', y: 28, opacity: 0 }, { clipPath: 'inset(0% 0 0 0)', y: 0, opacity: 1, duration: 1.0, ease: 'power4.out' }, '-=0.78')
      // The thesis TYPES itself in — mono, terminal, the second thing you read
      .fromTo(tagline, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.01 }, '-=0.5')
      .fromTo(taglineText, { width: 0 }, { width: 'auto', duration: 0.95, ease: 'steps(26)' }, '<')

    // ── The reticle HUNTS: sweeps in off-target, corrects, then SNAPS onto the
    //    face — `locked` flips on the actual snap, not a blind timer. ──
    intro
      .fromTo(
        reticle,
        { autoAlpha: 0, x: -vw * 0.14, y: -vh * 0.09, scale: 1.22 },
        { autoAlpha: 0.85, x: vw * 0.07, y: vh * 0.05, scale: 1.1, duration: 0.5, ease: 'power2.inOut' },
        '-=0.7',
      )
      .to(reticle, { x: -vw * 0.03, y: -vh * 0.02, scale: 1.04, duration: 0.32, ease: 'power2.inOut' })
      .to(reticle, {
        x: 0, y: 0, scale: 1, autoAlpha: 1, duration: 0.26, ease: 'power4.in',
        onComplete: () => setLocked(true),
      })
      // hard flash on the snap — the lock "tick"
      .fromTo(reticle, { filter: 'brightness(2.4)' }, { filter: 'brightness(1)', duration: 0.4, ease: 'power2.out' })

    // ── One scripted scan pass: the orange spotlight sweeps the name once, so the
    //    fire reveal is seen even by visitors who never move the mouse. Desktop only
    //    (on touch the reveal layer is disabled in CSS). ──
    if (vw >= 768) {
      const scan = { p: -30 }
      intro.to(scan, {
        p: 130, duration: 1.2, ease: 'power2.inOut',
        onUpdate: () => {
          ;[lineRef1.current, lineRef2.current].forEach((el) => {
            if (el) { el.style.setProperty('--mx', `${scan.p}%`); el.style.setProperty('--my', '50%') }
          })
        },
      }, '-=0.9')
    }

    intro
      .fromTo(coords, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }, '-=0.5')
      .fromTo(indicator, { opacity: 0 }, { opacity: 1, duration: 0.4 }, '-=0.15')

    return () => { intro.kill() }
  }, [isReady])

  // ── Mouse-tracking glow + orange text mask (desktop only) ──
  useEffect(() => {
    const sticky = stickyRef.current
    if (!sticky) return
    // On mobile the name is always fully orange — no cursor to track
    if (window.innerWidth < 768) return
    const onMove = (e: MouseEvent) => {
      // Glow follows cursor across whole sticky area
      if (glowRef.current) {
        const gr = sticky.getBoundingClientRect()
        const gx = ((e.clientX - gr.left) / gr.width) * 100
        const gy = ((e.clientY - gr.top) / gr.height) * 100
        glowRef.current.style.background =
          `radial-gradient(ellipse 50% 55% at ${gx}% ${gy}%, rgba(255,107,0,0.18) 0%, rgba(255,107,0,0.05) 50%, transparent 75%)`
      }
      // Orange mask on each name — move the ::after spotlight to the cursor by
      // setting CSS vars on the line (the pseudo-element reads --mx/--my).
      const applyMask = (el: HTMLSpanElement | null) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        const mx = ((e.clientX - r.left) / r.width) * 100
        const my = ((e.clientY - r.top) / r.height) * 100
        el.style.setProperty('--mx', `${mx}%`)
        el.style.setProperty('--my', `${my}%`)
      }
      applyMask(lineRef1.current)
      applyMask(lineRef2.current)
    }
    sticky.addEventListener('mousemove', onMove, { passive: true })
    return () => sticky.removeEventListener('mousemove', onMove)
  }, [])

  // ── Portrait rises in on mount — independent of preloader so LCP renders early ──
  useEffect(() => {
    const portrait = portraitRef.current
    if (!portrait) return
    const tween = gsap.fromTo(
      portrait,
      { opacity: 0, scale: 0.92, y: 60 },
      { opacity: 1, scale: 1, y: 0, duration: 1.5, delay: 0.4, ease: 'power2.out' },
    )
    return () => { tween.kill() }
  }, [])

  // ── The subject comes into FOCUS on the lock beat ──
  // Before lock the flesh half is faint + unfocused (the machine is "acquiring");
  // on the reticle SNAP it resolves to sharp — reticle "acquiring → lock" made
  // literal on the face. The name gives one fire pulse on the same frame so the
  // ring, the face and the headline all ignite together.
  useEffect(() => {
    if (!locked) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const flesh = containerRef.current?.querySelector<HTMLElement>('.hero-flesh')
    if (reduce) {
      if (flesh) gsap.set(flesh, { opacity: 1, filter: 'none' })
      return
    }
    if (flesh) {
      gsap.to(flesh, { opacity: 1, filter: 'blur(0px) saturate(1) brightness(1)', duration: 1.0, ease: 'power2.out' })
    }
    const l1 = lineRef1.current
    const l2 = lineRef2.current
    if (l1 && l2) {
      gsap.fromTo(
        [l1, l2],
        { textShadow: '0 0 24px rgba(255,107,0,0.5), 0 0 60px rgba(255,107,0,0.22)' },
        { textShadow: '0 0 0px rgba(255,107,0,0)', duration: 0.85, ease: 'power2.out' },
      )
    }
  }, [locked])

  // ── Respond to viewport resize for isMobile ──
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <section
      ref={containerRef}
      id="void"
      data-scene="void"
      className="relative w-full"
      style={{ height: '200vh', backgroundColor: 'var(--color-void)' }}
    >
      {/* Sticky viewport */}
      <div
        ref={stickyRef}
        className="relative w-full flex items-center justify-center overflow-hidden"
        style={{ height: '100dvh' }}
      >
        {/* Mouse-following radial glow */}
        <div
          ref={glowRef}
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 50% 55% at 50% 50%, rgba(255,107,0,0.18) 0%, rgba(255,107,0,0.05) 50%, transparent 75%)',
          }}
          aria-hidden="true"
        />

        {/* ── Targeting lock overlay (z-3, behind portrait z-4 + name z-10) — frames the
              subject; mirrors the portrait's centre WITHOUT touching #void-portrait. ── */}
        <div
          className="hero-lock absolute pointer-events-none"
          aria-hidden="true"
          style={{
            zIndex: 5,
            top: '47%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(50vh, 33rem, 78vw)',
            height: 'min(50vh, 33rem, 78vw)',
          }}
        >
          {/* rotating reticle — the hunt animates x/y/scale on this ring */}
          <div className="hero-lock-ring absolute inset-0" style={{ opacity: 0 }}>
            <Reticle />
          </div>

          {/* THE THESIS — promoted from caption size; types itself in on entrance */}
          <div
            className="hero-tagline font-mono uppercase"
            style={{
              position: 'absolute',
              top: '-2.7rem',
              left: '50%',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: 'clamp(0.66rem, 1.05vw, 1rem)',
              letterSpacing: '0.3em',
              color: 'var(--color-mist)',
              opacity: 0,
            }}
          >
            <span className="hero-tagline-text" style={{ display: 'inline-block', overflow: 'hidden', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              Forging the <span style={{ color: 'var(--color-fire)', textShadow: '0 0 20px rgba(255,107,0,0.45)' }}>Hextech</span> World
            </span>
            <span aria-hidden style={{ display: 'inline-block', width: '0.5ch', height: '0.95em', background: 'var(--color-fire)', marginLeft: 5, verticalAlign: '-0.08em', animation: 'blink 1.1s step-end infinite' }} />
          </div>

          {/* lock confirmation — appears only once the reticle has actually snapped */}
          <span
            className="font-mono uppercase"
            style={{
              position: 'absolute', bottom: '-1.7rem', left: '50%', transform: 'translateX(-50%)',
              whiteSpace: 'nowrap', fontSize: 'clamp(0.55rem, 0.7vw, 0.7rem)', letterSpacing: '0.26em',
              color: 'var(--color-fog)', opacity: locked ? 1 : 0, transition: 'opacity 0.45s ease',
            }}
          >
            &#9673; Identity Resolved
          </span>
        </div>

        {/* ── Split portrait — real photo left, orange dots right ── */}
        {/* id used by HexField to lock the particle portrait exactly onto this box */}
        <div
          ref={portraitRef}
          id="void-portrait"
          className="absolute pointer-events-none select-none"
          style={{
            zIndex: 8,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: isMobile ? 'clamp(340px, 94vw, 480px)' : 'clamp(420px, 50vw, 780px)',
            height: isMobile ? 'clamp(540px, 88dvh, 1040px)' : 'clamp(520px, 100vh, 1500px)',
          }}
          aria-hidden="true"
        >
          {/* Full portrait — real photo overlays dots. zIndex 8 so the face sits ABOVE
              the name (z-6) — the headline reads behind the person — but below the HUD
              chrome (z-10) so the status bar / coords are never occluded. */}
          <img
            src={fullPic}
            alt="Abdelrahman Abdelnasser — Software Engineer & AI Specialist from Cairo, portrait photo"
            fetchPriority="high"
            decoding="sync"
            className="hero-flesh absolute inset-0 w-full h-full object-cover object-top"
            style={{
              zIndex: 2,
              // Pre-lock: the subject is "acquiring" — faint + unfocused. Resolves to
              // sharp flesh on the reticle snap (see the `locked` effect).
              opacity: 0.32,
              filter: 'blur(2.5px) saturate(0.6) brightness(0.82)',
              willChange: 'opacity, filter',
              WebkitMaskImage: isMobile
                ? 'linear-gradient(to bottom, black 52%, transparent 90%), linear-gradient(to right, transparent, black 17%, black 83%, transparent)'
                : 'linear-gradient(to bottom, black 50%, transparent 88%), linear-gradient(to right, black 48%, transparent 78%)',
              maskImage: isMobile
                ? 'linear-gradient(to bottom, black 52%, transparent 90%), linear-gradient(to right, transparent, black 17%, black 83%, transparent)'
                : 'linear-gradient(to bottom, black 50%, transparent 88%), linear-gradient(to right, black 48%, transparent 78%)',
              WebkitMaskComposite: 'destination-in',
              maskComposite: 'intersect',
            }}
          />
          {/* Right half — orange dot art with gradient fade to left */}
          <img
            src={fullPicDots}
            alt="Abdelrahman Abdelnasser portrait rendered in orange particle dot art"
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 w-full h-full object-cover object-top"
            style={{
              zIndex: 1,
              WebkitMaskImage: isMobile
                ? 'linear-gradient(to bottom, black 52%, transparent 90%), linear-gradient(to right, transparent, black 17%, black 83%, transparent)'
                : 'linear-gradient(to bottom, black 70%, transparent 100%), linear-gradient(to left, black 20%, transparent 52%)',
              maskImage: isMobile
                ? 'linear-gradient(to bottom, black 52%, transparent 90%), linear-gradient(to right, transparent, black 17%, black 83%, transparent)'
                : 'linear-gradient(to bottom, black 70%, transparent 100%), linear-gradient(to left, black 20%, transparent 52%)',
              WebkitMaskComposite: 'destination-in',
              maskComposite: 'intersect',
            }}
          />
        </div>

        {/* ── HUD corner brackets (the acquired frame) ── */}
        <div className="absolute pointer-events-none" style={{ zIndex: 10, inset: edge }} aria-hidden="true">
          <span className="hero-corner" style={{ position: 'absolute', top: 0, left: 0, width: 18, height: 18, borderTop: '1px solid var(--color-fire)', borderLeft: '1px solid var(--color-fire)', opacity: 0 }} />
          <span className="hero-corner" style={{ position: 'absolute', top: 0, right: 0, width: 18, height: 18, borderTop: '1px solid var(--color-fire)', borderRight: '1px solid var(--color-fire)', opacity: 0 }} />
          <span className="hero-corner" style={{ position: 'absolute', bottom: 0, left: 0, width: 18, height: 18, borderBottom: '1px solid var(--color-fire)', borderLeft: '1px solid var(--color-fire)', opacity: 0 }} />
          <span className="hero-corner" style={{ position: 'absolute', bottom: 0, right: 0, width: 18, height: 18, borderBottom: '1px solid var(--color-fire)', borderRight: '1px solid var(--color-fire)', opacity: 0 }} />
        </div>

        {/* ── Status bar (replaces old top-left meta + top-centre role/status cluster) ── */}
        <div
          className="hero-statusbar absolute flex items-center justify-between flex-wrap gap-y-1"
          style={{ zIndex: 10, opacity: 0, top: `calc(${edge} + 3.5rem)`, left: edge, right: edge, gap: '0.75rem' }}
        >
          {/* left cluster: [00] ⬡ ABDELRAHMAN.A · SOFTWARE ENGINEER */}
          <div className="flex items-center gap-2.5 font-mono uppercase" style={{ minWidth: 0 }}>
            <span style={LBL}>[00]</span>
            <svg width="10" height="10" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
              <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" stroke="var(--color-fire)" strokeWidth="8" fill="none" />
            </svg>
            <span style={LBL}>Abdelrahman.A</span>
            <span style={LBL2}>&middot; Software Engineer</span>
          </div>
          {/* right cluster (hidden on small screens) */}
          <div className="hidden sm:flex items-center gap-3 font-mono uppercase" style={{ flexShrink: 0 }}>
            <span className="inline-flex items-center gap-1.5" style={LBL}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 8px var(--color-fire)', animation: 'dot-pulse 2.2s ease-in-out infinite' }} />
              Open to work
            </span>
            <span style={{ width: 1, height: 10, background: 'rgba(255,255,255,0.12)' }} />
            <span className="inline-flex items-center gap-1.5" style={{ ...LBL, color: locked ? 'var(--color-fog)' : 'var(--color-stone)' }}>
              <span
                aria-hidden
                style={{
                  width: 8, height: 9, clipPath: HEX_DOT,
                  background: locked ? 'var(--color-fire)' : 'var(--color-steel-deep)',
                  boxShadow: locked ? '0 0 9px var(--color-fire)' : 'none',
                  animation: locked ? 'dot-pulse 2.4s ease-in-out infinite' : 'none',
                }}
              />
              {locked ? 'Lock Acquired' : 'Acquiring'}
            </span>
            <span style={{ width: 1, height: 10, background: 'rgba(255,255,255,0.12)' }} />
            <span style={{ ...LBL, color: 'var(--color-fire)' }}>V.2.0.4</span>
          </div>
        </div>

        {/* ── Center content — name only ── */}
        <div
          ref={contentRef}
          className="relative flex flex-col items-center text-center"
          style={{ zIndex: 6, padding: `0 ${edge}` }}
        >
          {/* Single clean <h1> — orange reveal is a CSS ::after (see .hero-reveal in
              globals.css), so the h1 text content is exactly "Abdelrahman Abdelnasser". */}
          <h1 className="flex flex-col items-center">
            <span
              ref={lineRef1}
              data-text="Abdelrahman"
              className="hero-line-1 hero-reveal font-cunia font-bold uppercase leading-[0.85] tracking-[-0.03em]"
              style={{
                fontSize: nameSize,
                color: '#5C5A57',
                opacity: 0,
                display: 'block',
              }}
            >
              Abdelrahman
            </span>

            {' '}
            <span
              ref={lineRef2}
              data-text="Abdelnasser"
              className="hero-line-2 hero-reveal font-cunia font-bold uppercase leading-[0.85] tracking-[-0.03em]"
              style={{
                fontSize: nameSize,
                color: '#5C5A57',
                marginTop: '0.08em',
                opacity: 0,
                display: 'block',
              }}
            >
              Abdelnasser
            </span>
            {/* Descriptive context for the H1 — crawlers/AT read the full role;
                visually the hero shows just the name. */}
            <span className="sr-only"> — Software Engineer &amp; AI Specialist from Cairo, Egypt</span>
          </h1>
        </div>

        {/* ── Bottom-right coordinates (the lock's geotag) ── */}
        <div
          className="hero-coords absolute font-mono text-right uppercase"
          style={{
            zIndex: 10,
            opacity: 0,
            bottom: edge,
            right: edge,
          }}
        >
          <div
            style={{
              fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)',
              letterSpacing: '0.1em',
              color: 'var(--color-fog)',
            }}
          >
            30.0833&deg; N, 31.2333&deg; E
          </div>
          <div className="flex items-center justify-end gap-1.5" style={{ marginTop: '0.35rem' }}>
            <span
              style={{
                fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)',
                letterSpacing: '0.1em',
                color: 'var(--color-stone)',
              }}
            >
              Cairo, Egypt
            </span>
            <svg width="8" height="8" viewBox="0 0 100 100" fill="none">
              <polygon
                points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5"
                stroke="var(--color-stone)"
                strokeWidth="8"
                fill="none"
              />
            </svg>
          </div>
        </div>

        {/* ── Scroll indicator — bottom center ── */}
        <div
          ref={indicatorRef}
          className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          style={{ zIndex: 10, opacity: 0, bottom: edge }}
        >
          <div
            style={{
              width: '1px',
              height: 'clamp(24px, 3vh, 36px)',
              background: 'linear-gradient(to bottom, var(--color-fire), transparent)',
            }}
          />
          <svg
            width="8"
            height="8"
            viewBox="0 0 100 100"
            fill="none"
            style={{ animation: 'dot-pulse 2s ease-in-out infinite' }}
          >
            <polygon
              points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5"
              fill="var(--color-fire)"
            />
          </svg>
          <span
            className="font-mono uppercase"
            style={{ fontSize: '9px', letterSpacing: '0.3em', color: 'var(--color-fog)' }}
          >
            scroll
          </span>
        </div>
      </div>
    </section>
  )
}
